'use client';

import { useMutation } from '@ts-rest/react-query/tanstack';
import {
	EmailAuthProvider,
	reauthenticateWithCredential,
	updatePassword,
	confirmPasswordReset, verifyPasswordResetCode,
	getAuth,
} from 'firebase/auth';

import { app } from '@/libs/firebase/firebase';
import { tsr } from '@/libs/ts-rest/ts-rest';
import type { ChangePasswordFormModel } from '@/modules/authentication/view-model';

import { FirebaseAuthHandler } from '@modules/authentication';

import type { LoginDto } from '../dto/auth.dto';

export const useAuthMutations = () => {
	const registerMutation = tsr.auth.register.useMutation();
	const forgotPasswordMutation = tsr.auth.forgotPassword.useMutation();
	const updateProfileMutation = tsr.profile.update.useMutation();

	const logoutMutation = useMutation({
		mutationFn: async () => {
			const auth = getAuth(app);
			const firebaseAuthHandler = new FirebaseAuthHandler(auth);
			await firebaseAuthHandler.logout();
		},
	});

	const loginMutation = useMutation({
		mutationFn: async (form: LoginDto) => {
			const auth = getAuth(app);
			const firebaseAuthHandler = new FirebaseAuthHandler(auth);
			const credentials = await firebaseAuthHandler.signInWithEmail(form);
			const token = await credentials.user.getIdToken();
			const response = await tsr.auth.me.query({ headers: { authorization: `Bearer ${token}` } });

			if (response.status !== 200) throw new Error('Login Failed');

			return response;
		},
	});

	const changePasswordMutation = useMutation({
		mutationFn: async (form: ChangePasswordFormModel) => {
			const auth = getAuth(app);
			const user = auth.currentUser;

			if (!user || !user.email) throw new Error('No authenticated user found');

			const credential = EmailAuthProvider.credential(user.email, form.currentPassword);
			await reauthenticateWithCredential(user, credential);
			await updatePassword(user, form.newPassword);
			await user.reload();
			await user.getIdToken(true);
		},
	});

	const resetPasswordMutation = useMutation({
		mutationFn: async ({
			oob,
			password,
		}: {
			oob:      string
			password: string
		}) => {
			const auth = getAuth(app);
			await verifyPasswordResetCode(auth, oob);
			await confirmPasswordReset(auth, oob, password);
		},
	});

	return {
		changePasswordMutation,
		forgotPasswordMutation,
		loginMutation,
		logoutMutation,
		registerMutation,
		resetPasswordMutation,
		updateProfileMutation,
	};
};
